import { t } from 'i18next';

import { FormInputTypes } from '#/client';
import { FormInputVariants } from '#/service/constants';

import {
  InputField,
  InputFieldGroup,
  InputGroup,
  InputGroupType,
  InputPlanGroup,
  MainButtonGroupParams,
  SubmitButtonGroupParams,
} from './Form.types';

export const getMainErrorMessage = (
  validationEntries: [string, string[]][],
  responseMessage?: string
) => {
  return (
    // check if only one field has validation error(s)
    (validationEntries.length === 1 &&
      // check if that field has a single validation error
      // (optional chaining is a safety measure in case backend doesn't return an array)
      validationEntries[0][1]?.length === 1 &&
      // if so, use it
      validationEntries[0][1][0]) ||
    // otherwise, if there's an error message in the response, use that
    responseMessage ||
    // fall back to the generic error message as a last resort
    t('error.generic.message')
  );
};

export const createBasicFormConfig = (
  keyRoot: string,
  fields: InputField[],
  buttons: Omit<SubmitButtonGroupParams, 'hasSubmitButton'>
) => {
  return createFormConfig(
    keyRoot,
    { hasSubmitButton: true, ...buttons },
    {
      key: `${keyRoot}FieldGroup`,
      type: InputGroupType.Field,
      inputs: fields,
    }
  );
};

const createFormConfig = (
  keyRoot: string,
  buttons: SubmitButtonGroupParams | MainButtonGroupParams,
  primaryGroup: InputFieldGroup | InputPlanGroup
): InputGroup[] => {
  return [
    primaryGroup,
    {
      key: `${keyRoot}ButtonGroup`,
      type: InputGroupType.Button,
      inputs: [
        getPrimaryButton(keyRoot, buttons),
        getCancelButton(keyRoot, buttons),
      ].filter((b) => b),
    } as InputGroup, // TODO: why do we need this?
  ];
};

function getPrimaryButton(
  keyRoot: string,
  buttons: SubmitButtonGroupParams | MainButtonGroupParams
) {
  if (buttons.hasSubmitButton) {
    return {
      key: `${keyRoot}Submit`,
      type: FormInputTypes.INPUT,
      variant: FormInputVariants.SUBMIT,
      text: t(buttons.submit.textKeyPath),
      onClick: buttons.submit.onClick,
      disclaimer: buttons.submit.disclaimer,
    };
  }

  const tempButton = buttons;

  if (tempButton.main) {
    return {
      key: `${keyRoot}Main`,
      isMainButton: true,
      type: FormInputTypes.INPUT,
      variant: FormInputVariants.BUTTON,
      text: t(tempButton.main.textKeyPath),
      onClick: tempButton.main.onClick,
      disclaimer: tempButton.main.disclaimer,
    };
  }

  return null;
}

function getCancelButton(
  keyRoot: string,
  buttons: SubmitButtonGroupParams | MainButtonGroupParams
) {
  if (buttons.cancel) {
    return {
      key: `${keyRoot}Cancel`,
      type: FormInputTypes.INPUT,
      variant: FormInputVariants.BUTTON,
      text: t(buttons.cancel.textKeyPath || 'button.cancel'),
      onClick: buttons.cancel.onClick,
    };
  }

  return null;
}

/**
 * Try to locate the first input field in the form and focus it.
 * It uses small delay to make sure the form is rendered.
 */
export function focusFirstInput(el: HTMLDivElement | null) {
  if (el) {
    // TODO: Use callback ref to avoid setTimeout https://reactjs.org/docs/refs-and-the-dom.html#callback-refs
    setTimeout(() => {
      el?.querySelector('input')?.focus();
    }, 10);
  }
}
